import type { Group, User } from "../api/types";

export type GroupRole = "OWNER" | "MEMBER" | "VIEWER";

/**
 * The current user's role inside a group, by joining `me.id` with the
 * group's USER participants. Returns null when the user isn't a participant
 * (which the API normally prevents from reaching this view, but defensively
 * we treat that as no permissions rather than crashing).
 */
export function getMyGroupRole(
  group: Group,
  me: User | undefined,
): GroupRole | null {
  if (!me) return null;
  const self = group.participants.find(
    (p) => p.type === "USER" && p.userId === me.id,
  );
  return self?.role ?? null;
}

/**
 * The id of the participant row that represents the current user, by joining
 * `me.id` with the group's USER participants. Used to default "who paid" to
 * the person recording the expense. Returns undefined when the user isn't a
 * USER participant (e.g. still a guest), so callers fall back to the first
 * participant.
 */
export function getMyParticipantId(
  group: Group,
  me: User | undefined,
): string | undefined {
  if (!me) return undefined;
  return group.participants.find((p) => p.type === "USER" && p.userId === me.id)
    ?.id;
}

export function canManageGroup(role: GroupRole | null): boolean {
  return role === "OWNER";
}

export function canEditGroup(role: GroupRole | null): boolean {
  return role === "OWNER" || role === "MEMBER";
}

export function formatRole(role: GroupRole): string {
  return role.charAt(0) + role.slice(1).toLowerCase();
}
